import { Link } from "react-router";
import { errorMessage } from "../api/client";
import { useBootstrapQuery, usePropertiesQuery } from "../api/queries";
import { DirectoryList } from "../ui/DirectoryList";
import { DirectoryMyStores } from "../ui/VisitorAccess";
import { DirectoryRealEstate } from "../ui/realEstate/DirectoryRealEstate";
import { DirectoryHomes } from "../ui/homes/DirectoryHomes";
import { DirectoryTasks } from "../ui/rewards/EarnCoins";
import { PageShell } from "../ui/PageShell";

/**
 * 2D fallback (docs/00 section 5, docs/01 section 7): when WebGL is unavailable, `?no3d=1` was
 * requested, or the renderer failed, the town stays usable as an HTML directory: all three districts (ADR-0041),
 * filtered by district, street, type and availability.
 */
export function FallbackDirectory({
  reason,
  error,
  onRetry,
}: {
  reason: "requested" | "webgl" | "failed";
  error?: string | null;
  onRetry?: () => void;
}) {
  useBootstrapQuery(); // establishes the guest session so website links can be prepared
  const q = usePropertiesQuery(true);
  const title = reason === "requested" ? "Townsy in 2D" : reason === "webgl" ? "3D is not available on this device" : "The 3D town could not start";
  return (
    <PageShell title={title}>
      <div className="notice notice-info" role="status">
        {reason === "requested" ? (
          <p style={{ margin: 0 }}>
            You asked for the text version (<code>?no3d=1</code>). Every listing, website link and statistic is available here;{" "}
            <Link to="/">switch to the 3D town</Link> whenever you like.
          </p>
        ) : reason === "webgl" ? (
          <p style={{ margin: 0 }}>
            Your browser did not provide WebGL, which the 3D town needs. The directory below has the same businesses, website links and
            statistics. Walking, chat and avatars require the 3D view.
          </p>
        ) : (
          <>
            <p style={{ margin: 0 }}>
              The renderer reported: <code>{error ?? "unknown error"}</code>. You can keep using the directory below, or retry the 3D view.
            </p>
            {onRetry ? (
              <button type="button" className="btn btn-primary" style={{ marginTop: 8 }} onClick={onRetry}>
                Retry the 3D town
              </button>
            ) : null}
          </>
        )}
      </div>
      <div className="row" style={{ margin: "12px 0" }}>
        <Link className="btn btn-sm" to="/stats">
          Town statistics
        </Link>
        <Link className="btn btn-sm" to="/earn">
          Earn coins
        </Link>
        <Link className="btn btn-sm" to="/account">
          Account
        </Link>
      </div>
      {q.isPending ? (
        <p className="muted">Loading listings…</p>
      ) : q.error ? (
        <div className="notice notice-danger">
          <strong>Couldn't load the directory.</strong> {errorMessage(q.error)}{" "}
          <button type="button" className="btn btn-sm" onClick={() => void q.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <>
          {/* World v5 (ADR-0041): every district at first (All); no "Go there" here, each place opens its page. */}
          <DirectoryList items={q.data.items} initialDistrict={null} />
          <DirectoryMyStores items={q.data.items} />
        </>
      )}
      <DirectoryTasks />
      <DirectoryRealEstate />
      <DirectoryHomes />
    </PageShell>
  );
}
